import { observable } from 'mobx';
import { lastValueFrom, Observable, of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, map } from 'rxjs/operators';
import { container, singleton } from 'tsyringe';

import { DmsStore } from '../../dms';
import { LocalizationStore } from '../../localization';
import { DeviceStore, UserStore } from '../../user';

import { parseExponeaResponse } from './exponea.service-helper';

export type ExponeaUiButton = {
  position: string;
  text: string;
  type: string;
  link: string | null;
};

export type ExponeaUiResponse = {
  display: boolean;
  type: string;
  img?: string;
  title: string;
  main_text?: string;
  message?: string;
  buttons: ExponeaUiButton[];
  time: number;
  ratio?: '1:1' | '2:3' | '16:9';
};

@singleton()
export class ExponeaStore {
  @observable isReady = false;
  @observable bannerData: ExponeaUiResponse | null = null;

  async init(bannerType: string, pageName: string) {
    this.bannerData = await lastValueFrom(this.getExponeaUiData(bannerType, pageName));
    this.isReady = true;
  }

  private getExponeaUiData = (bannerType: string, pageName: string): Observable<ExponeaUiResponse | null> => {
    const { userData } = container.resolve<UserStore>('userStore');
    const { householdDevice } = container.resolve<DeviceStore>('deviceStore');
    const { dms } = container.resolve<DmsStore>('dmsStore');
    const localizationStore = container.resolve<LocalizationStore>('localizationStore');

    if (!dms.exponea.enable || !dms.exponea.endpointsUiUrl) {
      console.log('Exponea is disabled or dms.exponea.endpointsUiUrl is empty');
      return of(null);
    }

    return ajax({
      url: dms.exponea.endpointsUiUrl,
      body: JSON.stringify({
        customer_ids: {
          o2_id: 'xtv_customer',
        },
        params: {
          banner_type: bannerType,
          service_id: userData?.externalId,
          brand_id: householdDevice?.brandId,
          family_id: householdDevice?.deviceFamilyId,
          page: pageName,
          lang: localizationStore.displayLanguage,
        },
      }),
      headers: {
        Accept: 'application/json',
        Authorization: dms.exponea.authorization,
      },
      method: 'POST',
      crossDomain: true,
      withCredentials: false,
      // @ts-expect-error TODO Fix
      timeout: dms.exponea.requestTimeoutInSeconds * 1000,
    }).pipe(
      map((res) => parseExponeaResponse(res, dms)),
      catchError((err) => {
        console.log('Failed to load Exponea UI data', err);

        return of(null);
      }),
    );
  };

  resetStore() {
    this.isReady = false;
    this.bannerData = null;
  }
}
